import { BookingBuilderContainer } from '@components/BookingBuilder/BookingBuilderContainer';
import { BookingBuilderProgress } from '@components/BookingBuilder/BookingBuilderProgress';
import { OwnStorageFacilityPickupDialog } from '@components/BookingFlow/Common/OwnStorageFacilityPickupDialog';
import { StuddedTireSeasonDialog } from '@components/BookingFlow/Common/StuddedTireSeasonDialog';
import { TireHotelLocationSuggestionDialog } from '@components/BookingFlow/Common/TireHotelLocationSuggestionDialog';
import { ApiErrorMessageWithTrans } from '@components/Error/ApiErrorMessageWithTrans';
import { BookingRecordCreate } from '@navio/nsdk';
import { captureException } from '@sentry/react';
import { useMutationState } from '@tanstack/react-query';
import { NoddiAsyncReturnType, URLKeys } from 'noddi-async';
import { NoddiAsyncError } from 'noddi-types';
import { customerRoutes } from 'noddi-util';
import { trackBookingCreated, useBookingActions } from 'noddi-web';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrentBrand } from '../../../../stores/BrandStore/brandStore';
import { checkoutRetryFromMutationVariables } from '../../../../utils/stationaryCheckout';
import { useCreateBooking } from '../helpers/useCreateBooking';
import { useGetConfirmBookingMutationKey } from '../helpers/useGetConfirmBookingKey';

export function BookingBuilder() {
  const navigate = useNavigate();
  const confirmBookingMutationKey = useGetConfirmBookingMutationKey();
  const { clearStore } = useBookingActions();
  const { currentBrand } = useCurrentBrand();

  const bookingMutation = useMutationState({
    filters: { mutationKey: confirmBookingMutationKey }
  });

  const {
    createBooking,
    getTireHotelSuggestionPrompt,
    getOwnStorageFacilityPrompt,
    getStuddedTireSeasonPrompt,
    isPending
  } = useCreateBooking();

  // Keyed by attempt so a retry that fails with a new wheel pickup error prompts again.
  const [dismissedPromptAt, setDismissedPromptAt] = useState<number>();
  const latestAttempt = bookingMutation.at(-1);
  const failedAttempt =
    latestAttempt?.status === 'error' && latestAttempt.submittedAt !== dismissedPromptAt
      ? {
          error: latestAttempt.error as NoddiAsyncError,
          variables: latestAttempt.variables as BookingRecordCreate
        }
      : null;
  const dismissPrompt = () => setDismissedPromptAt(latestAttempt?.submittedAt);
  const tireHotelSuggestion = failedAttempt && getTireHotelSuggestionPrompt(failedAttempt, dismissPrompt);
  const ownStorageFacilityPickup = failedAttempt && getOwnStorageFacilityPrompt(failedAttempt, dismissPrompt);
  const studdedTireSeason = failedAttempt && getStuddedTireSeasonPrompt(failedAttempt, dismissPrompt);

  const isCreateBookingSuccessFull = bookingMutation.some((mutation) => mutation.status === 'success');
  // While one of these dialogs is open it owns the error surface; the builder must not render its own error screen behind it.
  const isCreateBookingError =
    !tireHotelSuggestion &&
    !ownStorageFacilityPickup &&
    !studdedTireSeason &&
    bookingMutation.some((mutation) => mutation.status === 'error');

  const data = bookingMutation.find((mutation) => mutation.status === 'success')?.data as NoddiAsyncReturnType<
    typeof URLKeys.postCreateBookingIgnoringImpersonation,
    typeof URLKeys.postCreateBookingIgnoringImpersonation
  >;

  useEffect(() => {
    if (isCreateBookingSuccessFull && data) {
      try {
        trackBookingCreated({
          brand: currentBrand,
          bookingId: data.data?.id,
          executingServiceDepartmentId: data.data?.serviceDepartment?.id,
          locationType: 'mobile',
          order: data.data?.order
        });
      } catch (error) {
        captureException(error);
      }
      clearStore();
      navigate(customerRoutes.confirmation.getPath({ slug: data.data.slug }), { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- legacy: reason not recorded; tracks the booking, clears the store and redirects when the booking mutation succeeds
  }, [isCreateBookingSuccessFull]);

  return (
    <>
      {tireHotelSuggestion && <TireHotelLocationSuggestionDialog {...tireHotelSuggestion} isPending={isPending} />}
      {ownStorageFacilityPickup && <OwnStorageFacilityPickupDialog {...ownStorageFacilityPickup} />}
      {studdedTireSeason && <StuddedTireSeasonDialog {...studdedTireSeason} isPending={isPending} />}
      <BookingBuilderContainer
        isError={isCreateBookingError}
        isPaused={isCreateBookingError && !isPending}
        errorButton={(handleRestart) => (
          <div className='mt-4'>
            <ApiErrorMessageWithTrans
              error={bookingMutation.find((mutation) => mutation.status === 'error')?.error as NoddiAsyncError}
              isRetrying={isPending}
              onRetry={() => {
                handleRestart();
                const failed = bookingMutation.find((mutation) => mutation.status === 'error');
                const retry = checkoutRetryFromMutationVariables(failed?.variables);
                // oxlint-disable-next-line typescript/no-floating-promises -- mutateAsync result unused; the retry failure is rendered from the mutation state above
                createBooking({
                  paymentMethod: retry.paymentMethod,
                  userGroupPayerId: retry.userGroupPayerId,
                  bookingUserGroupId: retry.userGroupId,
                  invoiceReference: retry.invoiceReference ?? undefined,
                  bookedForUserId: retry.userId
                });
              }}
            />
          </div>
        )}
      >
        {({ progress }) => <BookingBuilderProgress progress={progress} />}
      </BookingBuilderContainer>
    </>
  );
}
